import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import formatDate from '@ps-int/ufo-helpers/lib/date/date-to-string';
import { Empty } from '@yandex-360/orb-components/dist/components/Empty';
import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../init-store';
import { OutgoingEvents } from '../../../pages/call/iframe-sagas/types';
import { getMyUserId, getRoomId } from '../../../pages/call/selectors';
import { checkHasDesktopAppAction, setErrorPageAction } from '../../features/config';
import {
    getPageClassNameSelector,
    isCoverLayerEnabled,
    isLightTheme,
    isMessengerIntegration,
    selectErrorPageReason,
    selectIsGlobalBarEnabled,
} from '../../features/config/selectors';
import { parentFrameCommunicator } from '../../features/parent-frame-communicator';
import { PremiumButtonHeader } from '../../features/root/root-container/components/default-view/components/premium-button-header';
import { getPremiumButtonEnabled } from '../../features/root/selectors';
import { getUserSelector, toPassport } from '../../features/user';
import { selectIsUserB2BDomainMismatch } from '../../features/user/selectors';
import { LoggableError } from '../../helpers/errors';
import { withErrorBoundary } from '../../helpers/hocs/with-error-boundary';
import { i18n, i18nMonthsGenitive } from '../../helpers/i18n';
import { ACTION, FEATURE } from '../../helpers/logger-enums';
import { createLogger } from '../../helpers/loggerFacade';
import { countMetrika } from '../../helpers/metrika';
import { SCREENS, useNavigation } from '../../helpers/navigation';
import Button from '../button';
import CoverLayer from '../cover-layer';
import Footer from '../footer';
import { ThemeBackground } from '../theme-background';
import { UnsupportedMessengerPage } from './errors/unsupported-messenger';
import UnsupportedBrowserPage from './errors/unsupportedBrowser';
import { UnsupportedTouchErrorPage } from './errors/unsupportedTouch';
import css from './index.css';
import { ErrorPageType } from './types';

enum ForceButtonTypeEnum {
    CONNECT = 'CONNECT',
}

const MAX_TIMER_WAITING_TIME = 10 * 60 * 60 * 1000; // 10 часов

const getRealStartEventTime = (time: number) => {
    // за 5 минут до начала встречи из календаря
    return time - 5 * 60 * 1000;
};

const mapState = (state: RootState) => {
    const {
        config: { errorPageType, errorPageParams },
        ua: { isMobile, BrowserName },
        user,
        sessionId,
    } = state;

    return {
        pageType: errorPageType,
        errorPageParams,
        isMobile,
        BrowserName,
        sessionId,
        pageClassName: getPageClassNameSelector(state),
        isMessengerIntegration: isMessengerIntegration(state),
        isAuthorized: Boolean(getUserSelector(state)),
        isYandexTeamUser: Boolean(user?.isYandexTeamUser),
        isRaisedButton: isLightTheme(state),
        isUserDomainMismatch: selectIsUserB2BDomainMismatch(state),
        roomId: getRoomId(state),
        userId: getMyUserId(state),
    };
};

const mapDispatch = {
    setErrorPage: setErrorPageAction,
    checkHasDesktopAppAction,
    toPassport,
};

const connector = connect(mapState, mapDispatch);

const logger = createLogger('error-page');

type ErrorPageConnectedProps = ConnectedProps<typeof connector>;

const ErrorPageComponent: React.FunctionComponent<ErrorPageConnectedProps> = (props) => {
    const { pageType } = props;
    const errorPageType = pageType;
    if (errorPageType === ErrorPageType.UNSUPPORTED_TOUCH) {
        return <UnsupportedTouchErrorPage BrowserName={props.BrowserName} />;
    }
    return <DefaultErrorPage {...props} pageType={errorPageType} />;
};

const onButtonClickInMessenger = () => {
    parentFrameCommunicator.sendMessage('messenger', { event: OutgoingEvents.ERROR_PAGE_BUTTON_CLICK });
};

const DefaultErrorPage: React.FunctionComponent<ErrorPageConnectedProps> = ({
    pageType,
    isMobile,
    setErrorPage,
    checkHasDesktopAppAction,
    sessionId,
    errorPageParams,
    pageClassName,
    isMessengerIntegration,
    toPassport,
    isAuthorized,
    isYandexTeamUser,
    isUserDomainMismatch,
    roomId,
    userId,
}) => {
    const { replace } = useNavigation();
    const errorPageType = pageType;
    const [forceButtonType, setForceButtonType] = useState<ForceButtonTypeEnum | null>(null);
    const handleLoginClick = useCallback(() => toPassport(), [toPassport]);

    const isGlobalBarEnabled = useSelector(selectIsGlobalBarEnabled);

    const isBroadcastEnded = errorPageType === ErrorPageType.BROADCAST_GONE && errorPageParams?.ended;

    useEffect(() => {
        let timeOutId: number;
        if (errorPageType === ErrorPageType.NOT_STARTED) {
            const startEventTime = Number(errorPageParams?.startEventTime);
            if (startEventTime) {
                const timeDelta = getRealStartEventTime(startEventTime) - Number(new Date());
                if (timeDelta < MAX_TIMER_WAITING_TIME) {
                    // если до начала встречи меньше 10 часов, то запускаем таймер на смену кнопки
                    timeOutId = window.setTimeout(() => {
                        setForceButtonType(ForceButtonTypeEnum.CONNECT);
                    }, timeDelta);
                }
            }
        }
        return () => {
            window.clearTimeout(timeOutId);
        };
    }, [errorPageType, errorPageParams?.startEventTime]);

    const errorPageReason = useSelector(selectErrorPageReason);
    useEffect(() => {
        if (!errorPageType) {
            return;
        }

        countMetrika('error page', errorPageType, sessionId);
        logger.warn(
            new LoggableError('Error page shown', {
                additional: {
                    errorPageType,
                    errorPageReason,
                },
            })
        );
    }, [errorPageReason, errorPageType, sessionId]);

    useEffect(() => {
        if (errorPageType === ErrorPageType.UNSUPPORTED_BROWSER) {
            checkHasDesktopAppAction();
        }
    }, [errorPageType, checkHasDesktopAppAction]);

    const retryText = useMemo(() => {
        if (forceButtonType === ForceButtonTypeEnum.CONNECT) {
            return i18n('create-call', 'connect');
        }

        if (errorPageType === ErrorPageType.NOT_IN_EXPERIMENT) {
            return i18n('common', 'back');
        }

        if (
            errorPageType === ErrorPageType.GONE ||
            errorPageType === ErrorPageType.NOT_FOUND ||
            errorPageType === ErrorPageType.NOT_STARTED ||
            errorPageType === ErrorPageType.BROADCAST_NOT_FOUND ||
            errorPageType === ErrorPageType.BROADCAST_GONE
        ) {
            return i18n('common', 'ok');
        }
        if (
            errorPageType === ErrorPageType.LIMIT_USERS_COUNT ||
            errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES ||
            errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES ||
            errorPageType === ErrorPageType.USER_KECKED ||
            errorPageType === ErrorPageType.USER_KICKED_WITH_BAN
        ) {
            return i18n('common', 'to-home-screen');
        }

        return i18n('common', 'retry');
    }, [errorPageType, forceButtonType]);

    const onRetry = useCallback(() => {
        if (errorPageType === ErrorPageType.DEFAULT || forceButtonType === ForceButtonTypeEnum.CONNECT) {
            logger.featureEvent({
                feature: FEATURE.ERROR_PAGE,
                action: ACTION.RETRY_RELOAD,
                options: {
                    additional: {
                        errorPageType,
                        forceButtonType,
                        roomId,
                        userId,
                    },
                },
            });
            window.location.reload();
        } else if (
            errorPageType === ErrorPageType.NOT_FOUND ||
            errorPageType === ErrorPageType.BROADCAST_NOT_FOUND ||
            errorPageType === ErrorPageType.BROADCAST_GONE ||
            errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES ||
            errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES ||
            errorPageType === ErrorPageType.USER_KECKED ||
            errorPageType === ErrorPageType.USER_KICKED_WITH_BAN ||
            isUserDomainMismatch
        ) {
            logger.featureEvent({
                feature: FEATURE.ERROR_PAGE,
                action: ACTION.RETRY_GO_HOME,
                options: {
                    additional: {
                        errorPageType,
                        isUserDomainMismatch,
                    },
                },
            });
            window.location.assign('/');
        } else {
            logger.featureEvent({
                feature: FEATURE.ERROR_PAGE,
                action: ACTION.RETRY_CREATE_CALL,
                options: {
                    additional: {
                        errorPageType,
                    },
                },
            });
            replace(SCREENS.CREATE_CALL, { searchString: window.location.search });
            setErrorPage(null);
        }
    }, [errorPageType, replace, setErrorPage, forceButtonType, isUserDomainMismatch, roomId, userId]);

    const actionButton = useMemo(() => {
        if (isMessengerIntegration) {
            return (
                <Button
                    key="button"
                    view="neutral"
                    size="lg"
                    onClick={onButtonClickInMessenger}
                    className={cn(css.errorButton, css.errorButtonCommon)}
                >
                    {i18n('error-page', 'back-to-chats')}
                </Button>
            );
        }

        if (errorPageType === ErrorPageType.UNSUPPORTED_BROWSER) {
            return null;
        }

        if (errorPageType === ErrorPageType.FORBIDDEN) {
            // В вебе не должны показывать кнопку
            return null;
        }

        if (errorPageType === ErrorPageType.UNSUPPORTED_ELECTRON) {
            return (
                <Button
                    key="button"
                    view="brand"
                    size="lg"
                    elementType="a"
                    href="/download-desktop"
                    target="_blank"
                    className={css.errorButton}
                >
                    {i18n('error-page', 'unsupported-electron-button')}
                </Button>
            );
        }

        if (
            (errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES ||
                errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES) &&
            isAuthorized &&
            isYandexTeamUser
        ) {
            return (
                <>
                    <Button key="login-button" view="brand" size="lg" onClick={handleLoginClick} className={css.button}>
                        {i18n('error-page', 'organization-mismatches-yateam-button-primary')}
                    </Button>
                    <Button
                        key="button"
                        view="neutral"
                        size="lg"
                        onClick={() => checkHasDesktopAppAction()}
                        className={cn(css.button)}
                    >
                        {i18n('error-page', 'organization-mismatches-yateam-button-secondary')}
                    </Button>
                </>
            );
        }

        if (
            (errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES ||
                errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES) &&
            isAuthorized &&
            !isYandexTeamUser
        ) {
            return (
                <>
                    <Button key="button" view="brand" size="lg" onClick={onRetry} className={cn(css.button)}>
                        {retryText}
                    </Button>
                    <Button
                        key="login-button"
                        view="neutral"
                        size="lg"
                        onClick={handleLoginClick}
                        className={css.button}
                    >
                        {i18n('common', 'change-account')}
                    </Button>
                </>
            );
        }

        if (
            (errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES ||
                errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES) &&
            !isAuthorized
        ) {
            return (
                <Button key="login-button" view="brand" size="lg" onClick={handleLoginClick} className={css.button}>
                    {i18n('error-page', 'organization-mismatches-unauthorized-button')}
                </Button>
            );
        }

        return (
            <Button
                key="button"
                view="neutral"
                size="lg"
                onClick={onRetry}
                className={cn(css.errorButton, css.errorButtonCommon)}
            >
                {retryText}
            </Button>
        );
    }, [
        isMessengerIntegration,
        errorPageType,
        isAuthorized,
        isYandexTeamUser,
        onRetry,
        retryText,
        handleLoginClick,
        checkHasDesktopAppAction,
    ]);

    const title = useMemo(() => {
        if (errorPageType === ErrorPageType.UNSUPPORTED_BROWSER) {
            return i18n('error-page', 'usupported-browser-title');
        }

        if (errorPageType === ErrorPageType.UNSUPPORTED_ELECTRON) {
            return i18n('error-page', 'unsupported-electron');
        }

        if (errorPageType === ErrorPageType.FORBIDDEN) {
            return i18n('error-page', 'forbidden-title');
        }

        if (errorPageType === ErrorPageType.LIMIT_USERS_COUNT) {
            return i18n('statuses', 'participants-limit');
        }

        if (errorPageType === ErrorPageType.GONE) {
            return i18n('error-page', 'gone-title');
        }

        if (errorPageType === ErrorPageType.NOT_IN_EXPERIMENT) {
            return i18n('error-page', 'not-in-experiment-title');
        }

        if (errorPageType === ErrorPageType.NOT_FOUND) {
            return i18n('error-page', 'not-found-title');
        }

        if (errorPageType === ErrorPageType.BROADCAST_GONE) {
            return i18n('error-page', 'broadcast-gone');
        }

        if (errorPageType === ErrorPageType.BROADCAST_NOT_FOUND) {
            return i18n('error-page', 'broadcast-not-found');
        }

        if (errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES) {
            if (isYandexTeamUser) {
                return i18n('error-page', 'organization-mismatches-yateam-title');
            }

            return i18n('error-page', 'organization-mismatches-title');
        }

        if (errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES) {
            if (isYandexTeamUser) {
                return i18n('error-page', 'organization-mismatches-yateam-title');
            }

            return i18n('error-page', 'broadcast-organization-mismatches-title');
        }

        if (errorPageType === ErrorPageType.USER_KECKED || errorPageType === ErrorPageType.USER_KICKED_WITH_BAN) {
            return i18n('error-page', 'user-kicked-title');
        }

        const startEventTime = Number(errorPageParams?.startEventTime);

        if (errorPageType === ErrorPageType.NOT_STARTED && startEventTime) {
            return i18n('error-page', 'not-started-title', {
                date: formatDate(startEventTime, 'DD mmmm YYYY', [], i18nMonthsGenitive()),
                time: formatDate(startEventTime, 'HH:mm'),
                startTime: formatDate(getRealStartEventTime(startEventTime), 'HH:mm'),
            });
        }

        if (errorPageType === ErrorPageType.OVERLOADED) {
            return i18n('error-page', 'overloaded-title');
        }

        return i18n('error-page', 'default-title');
    }, [errorPageType, errorPageParams?.startEventTime, isYandexTeamUser]);

    const wideTitle = errorPageType && [ErrorPageType.NOT_STARTED].includes(errorPageType);

    const description = React.useMemo(() => {
        if (errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES && isAuthorized && isYandexTeamUser) {
            return i18n('error-page', 'organization-mismatches-yateam-description');
        }

        if (errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES && isAuthorized && !isYandexTeamUser) {
            return i18n('error-page', 'organization-mismatches-description');
        }

        if (errorPageType === ErrorPageType.ORGANIZATION_MISMATCHES && !isAuthorized) {
            return i18n('error-page', 'organization-mismatches-unauthorized-description');
        }

        if (errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES && isAuthorized && isYandexTeamUser) {
            return i18n('error-page', 'organization-mismatches-yateam-description');
        }

        if (errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES && isAuthorized && !isYandexTeamUser) {
            return i18n('error-page', 'broadcast-organization-mismatches-description');
        }

        if (errorPageType === ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES && !isAuthorized) {
            return i18n('error-page', 'broadcast-organization-mismatches-unauthorized-description');
        }

        if (errorPageType === ErrorPageType.USER_KECKED) {
            return i18n('error-page', 'user-kicked-description');
        }

        if (errorPageType === ErrorPageType.USER_KICKED_WITH_BAN) {
            return i18n('error-page', 'user-kicked-with-ban-description');
        }

        return null;
    }, [errorPageType, isAuthorized, isYandexTeamUser]);

    let content;

    if (errorPageType === ErrorPageType.UNSUPPORTED_BROWSER) {
        content = <UnsupportedBrowserPage />;
    } else if (errorPageType === ErrorPageType.UNSUPPORTED_MESSENGER) {
        content = <UnsupportedMessengerPage />;
    } else if (isBroadcastEnded) {
        const text = (() => {
            if (typeof errorPageParams?.endedAt === 'string' || typeof errorPageParams?.endedAt === 'number') {
                const endingDatetime =
                    typeof errorPageParams?.endedAt === 'string'
                        ? new Date(errorPageParams?.endedAt)
                        : errorPageParams?.endedAt * 1000;
                return i18n('error-page', 'broadcast-gone-v2-with-datetime', {
                    date: formatDate(endingDatetime, 'DD mmmm', [], i18nMonthsGenitive()),
                    time: formatDate(endingDatetime, 'HH:mm', [], i18nMonthsGenitive()),
                });
            }
            return i18n('error-page', 'broadcast-gone-v2');
        })();
        content = (
            <Empty
                title={text}
                button={
                    <Button
                        key="broadcast-ended-button"
                        view="neutral"
                        size="md"
                        onClick={onRetry}
                        className={css.errorButtonBroadcastEnded}
                    >
                        {i18n('common', 'to-home')}
                    </Button>
                }
            />
        );
    } else {
        content = (
            <>
                <div
                    key="title"
                    className={cn(css.title, {
                        [css.wideTitle]: wideTitle,
                    })}
                >
                    {title}
                </div>
                {description ? (
                    <div key="description" className={cn(css.description)}>
                        {description}
                    </div>
                ) : null}
                {actionButton}
            </>
        );
    }

    const cssContainer = cn(css.container, {
        [css.containerWithoutFooter]: !isMobile && errorPageType === ErrorPageType.UNSUPPORTED_BROWSER,
        [css.containerWithoutHeaderAndFooter]: isGlobalBarEnabled,
    });

    const cssContent = cn(css.content, {
        [css.contentWithMargin]:
            errorPageType !== ErrorPageType.UNSUPPORTED_BROWSER &&
            (errorPageType !== ErrorPageType.UNAUTHORIZED || !isMessengerIntegration) &&
            !isBroadcastEnded,
        [css.contentCentering]: isBroadcastEnded,
    });

    const shouldRenderCover = useSelector(isCoverLayerEnabled);

    const ref = useRef<HTMLDivElement>(null);

    const shouldShowFooter =
        !isMobile &&
        errorPageType !== ErrorPageType.UNSUPPORTED_BROWSER &&
        !isMessengerIntegration &&
        !isGlobalBarEnabled;
    const isPremiumButtonEnabled = useSelector(getPremiumButtonEnabled);
    return (
        <>
            {shouldRenderCover ? <CoverLayer /> : null}
            <ThemeBackground />
            <div className={cn(cssContainer, pageClassName)} ref={ref}>
                {isPremiumButtonEnabled && <PremiumButtonHeader className={css.premiumButtonHeader} />}
                <div className={cssContent}>{content}</div>
                {shouldShowFooter ? <Footer /> : null}
            </div>
        </>
    );
};

export const ErrorPage = withErrorBoundary(connector(ErrorPageComponent));
